import { FC, useMemo, useCallback, useState, ChangeEvent } from 'react';
import { Loading, Section, Input, Button, Table, ReportCard } from '../../../../components';
import { formataPlaca, moneyFormatter } from '../../../../utils';
import { useToastStore } from '../../../../stores';
import {
	getAllPayment,
	TAnuncioPagamento
} from '../../../../services';
import { Line } from 'react-chartjs-2';
import {
	Form,
	FullLine,
	HalfLine,
	HalfLineButton,
	NoData,
	Separator,
	TableContainer,
	TableWrapper,
	TitleContainer,
} from '../../Reports.styles';

const AdminPaymentsDefaultersReport: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState(false);
	const [filteredOffers, setFilteredOffers] = useState<TAnuncioPagamento[]>([]);
	const [placa, setPlaca] = useState('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [clickGenerate, setClickGenerate] = useState(false);

	const handleSetPlaca = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { value } = e.target;
		const newPlaca = formataPlaca(value);
		setPlaca(newPlaca)
	};

	const fetchData = async () => {
		setLoading(true);
		let body = {};
		if (startDate && endDate && endDate >= startDate) {
			body = {
				dataCadastroInicial: `${startDate}T00:00:00.000Z`,
				dataCadastroFinal: `${endDate}T23:59:59.999Z`,
			};
		}
		const data = await getAllPayment(body);
		if (!data) {
			setToast({ variant: 'error', message: 'Erro ao buscar dados!' });
			setLoading(false);
			return;
		}

		const filtered = data.filter((payment) => {
			const matchesPlate = !placa || payment.anuncio!.placa.includes(placa);
			const isPaymentEmpty = !payment.dataPagamento;

			return matchesPlate && isPaymentEmpty;
		}).sort((a, b) => (b.dataCadastro && a.dataCadastro) ? new Date(b.dataCadastro).getTime() - new Date(a.dataCadastro).getTime() : 0);

		setFilteredOffers(filtered);
		setClickGenerate(true);
		setLoading(false);
	};

	const tableColumns = useMemo(
		() => [

			{ id: 'registerDate', label: 'Data de Cobrança', width: '100px' },
			{ id: 'plate', label: 'Placa' },
			{ id: 'dealer', label: 'Anunciante' },
			{ id: 'buyer', label: 'Comprador' },
			{ id: 'value', label: 'Valor Cobrado' },
		],
		[]
	);

	const rowMapper = useCallback(
		(payment: TAnuncioPagamento) => ({
			plate: {
				value: payment.anuncio?.placa,
				rawValue: payment.anuncio?.placa,
			},
			dealer: {
				value: payment.anuncio?.concessionaria.fantasia,
				rawValue: payment.anuncio?.concessionaria.fantasia,
			},
			buyer: {
				value: payment.revenda.fantasia,
				rawValue: payment.revenda.fantasia,
			},
			registerDate: {
				value: new Date(payment.dataCadastro!).toLocaleDateString('pt-BR'),
				rawValue: new Date(payment.dataCadastro!).getTime(),
			},
			value: {
				value: moneyFormatter(payment.valorCobrado),
				rawValue: payment.valorCobrado,
			},
		}),
		[]
	);

	const MemoizedTable = useMemo(
		() => <Table columns={tableColumns} rows={filteredOffers?.map(rowMapper)} paginate exportToPDF exportToCSV />,
		[filteredOffers, rowMapper, tableColumns]
	);

	const pagamentosPendentes = filteredOffers.length.toString();
	const valorPendente = filteredOffers.reduce((total, curr) => total + (curr.valorCobrado || 0), 0);

	return (
		<Section>
			<TitleContainer>Relatório de Pagamentos</TitleContainer>
			<Separator />
			<Form>
				<HalfLine>
					<Input
						label="Data de Cobrança Inicial"
						type="date"
						variant='text'
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Placa"
						placeholder="Placa"
						type="text"
						variant='text'
						value={placa}
						onChange={handleSetPlaca}
						maxLength={8}
					/>
				</HalfLine>
				<HalfLineButton>
					<FullLine>
						<HalfLineButton>
							<Button
								variant='large'
								fitContent
								onClick={fetchData}
								disabled={(!startDate || !endDate) && !placa}
							>
								Gerar
							</Button>
						</HalfLineButton>
					</FullLine>
				</HalfLineButton>
				<HalfLine>
					<Input
						label="Data de Cobrança Final"
						type="date"
						variant='text'
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						min={startDate}
					/>
				</HalfLine>
			</Form>
			{loading && <Loading />}
			{filteredOffers.length > 0 && (
				<ReportCard
					data={[
						{
							color: '#f44336',
							title: 'Valor Pagamentos Pendentes no Período',
							value: moneyFormatter(valorPendente),
						},
						{
							color: '#FF9800',
							title: 'Qtde Pagamentos Pendentes no Período',
							value: pagamentosPendentes.length,
						}
					]}
				/>

			)}
			<TableContainer>
				{(loading || (filteredOffers?.length || 0) > 0) && (
					<TableWrapper>{loading ?  <Loading /> : MemoizedTable}</TableWrapper>
				)}
				{!loading && clickGenerate && filteredOffers?.length === 0 && (
					<NoData>Nenhum registro encontrado</NoData>
				)}
			</TableContainer>
			
		</Section>
	);
};

export default AdminPaymentsDefaultersReport;
